import { RefundIneligibilityReason, RefundRequestReasonCode } from '@repo/frontend-shared';

/** The member's own words, as the refund form offers them. */
export const REFUND_REASON_LABELS: Record<RefundRequestReasonCode, string> = {
  [RefundRequestReasonCode.NOT_AS_EXPECTED]: 'It wasn’t what I expected',
  [RefundRequestReasonCode.TECHNICAL_OR_ACCESS_ISSUES]: 'I experienced technical or access issues',
  [RefundRequestReasonCode.CHARGED_INCORRECTLY]: 'I was charged incorrectly',
  [RefundRequestReasonCode.NO_TIME_TO_COMPLETE]: 'I no longer have time to complete it',
  [RefundRequestReasonCode.OTHER]: 'Other',
};

/**
 * Only "Other" asks for detail: the other reasons say enough on their own, and
 * the box is where a member explains the one reason the list could not name.
 */
export function asksForReasonDetail(reasonCode: RefundRequestReasonCode | undefined): boolean {
  return reasonCode === RefundRequestReasonCode.OTHER;
}

export const REFUND_REASON_DETAIL_MAX_LENGTH = 1300;

export function describeRefundReason(reasonCode: RefundRequestReasonCode | null | undefined): string | undefined {
  return reasonCode ? REFUND_REASON_LABELS[reasonCode] : undefined;
}

/** "7 days remaining" — the window as the modal states it. */
export function describeDaysRemaining(daysRemaining: number): string {
  return `${daysRemaining} ${daysRemaining === 1 ? 'day' : 'days'} remaining`;
}

export function describeCompletionRate(completionRatePercent: number): string {
  return `${completionRatePercent}%`;
}

/**
 * Whether the member is under the completion cap.
 *
 * Read from the two figures rather than from `isEligible`: the pill reports
 * the completion rule alone, and a refund denied for some other reason still
 * shows "Below limit" when completion was never the problem.
 */
export function isBelowCompletionLimit(completionRatePercent: number, completionLimitPercent: number): boolean {
  return completionRatePercent < completionLimitPercent;
}

/**
 * Why the refund was refused, in the member's terms.
 *
 * Each names the rule and then says what does *not* change, because the
 * question behind "why not" is usually "so what happens to what I have".
 */
export function describeIneligibility(
  reason: RefundIneligibilityReason | null | undefined,
  completionLimitPercent: number,
  windowDays: number,
): string {
  switch (reason) {
    case RefundIneligibilityReason.ALREADY_REFUNDED:
      return 'This purchase has already been refunded. Your access and scheduled payments will continue unchanged.';
    case RefundIneligibilityReason.CERTIFICATE_ISSUED:
      return 'This purchase is not eligible under the money-back guarantee once a certificate has been issued. Your access and scheduled payments will continue unchanged.';
    case RefundIneligibilityReason.WINDOW_CLOSED:
      return `This purchase is not eligible under the money-back guarantee. The ${windowDays}-day window has closed. Your access and scheduled payments will continue unchanged.`;
    case RefundIneligibilityReason.COMPLETION_LIMIT_REACHED:
      return `This purchase is not eligible under the money-back guarantee. Completion must be below ${completionLimitPercent}% while the ${windowDays}-day window is still open. Your access and scheduled payments will continue unchanged.`;
    case RefundIneligibilityReason.INSTALLMENT_WINDOW_PASSED:
      return 'A payment plan can only be refunded during its first month. Your access and scheduled payments will continue unchanged.';
    default:
      return 'This purchase is not eligible under the money-back guarantee. Your access and scheduled payments will continue unchanged.';
  }
}
